import { ExternalKeysStatusSchema, type ExternalKeysStatusType, type ExternalKeysTypeType } from "@repo/types";
import { db } from "../client";

export async function getExternalKeys(type?: ExternalKeysTypeType) {
  return await db.externalKeys.findMany({
    where: type ? { type } : {},
    orderBy: [
      { usage: 'asc' },
      { usageCount: 'asc' }
    ]
  });
}

export async function getExternalKeyById(id: string) {
  return await db.externalKeys.findFirst({
    where: { id }
  });
}

export async function getExternalKeysWithEnhancedSelection(type: ExternalKeysTypeType, status : ExternalKeysStatusType[] = [ExternalKeysStatusSchema.Values.ACTIVE]) {
  const now = new Date();
  
  // First, let's try to get all active keys of this type
  const allKeys = await db.externalKeys.findMany({
    where: {
      type,
      status: { in: status }
    },
    orderBy: [
      { monthlyUsage: 'asc' },           // Primary: Cost efficiency
      { monthlyUsageCount: 'asc' },      // Secondary: Monthly fairness
      { lastUsedDate: 'asc' },           // Tertiary: Time-based fairness
      { usageCount: 'asc' }              // Quaternary: Historical fairness
    ]
  });

  // Filter out keys that are currently in cooldown
  const activeKeys = allKeys.filter(key => {
    if (!key.cooldownUntil) return true; // null or undefined
    return key.cooldownUntil < now; // expired cooldown
  });

  return activeKeys;
}

export async function resetMonthlyUsage() {
  const now = new Date();
  
  return await db.externalKeys.updateMany({
    where: {},
    data: {
      monthlyUsageCount: 0,
      monthlyUsage: 0,
      lastResetDate: now,
      cooldownUntil: null
    }
  });
}

export async function updateExternalKeyUsage(
  id: string, 
  usage: number, 
  monthlyUsageCount?: number, 
  monthlyUsage?: number
) {
  const updateData: any = { usage };
  
  if (monthlyUsageCount !== undefined) {
    updateData.monthlyUsageCount = monthlyUsageCount;
  }
  
  if (monthlyUsage !== undefined) {
    updateData.monthlyUsage = monthlyUsage;
  }
  
  if (monthlyUsageCount !== undefined || monthlyUsage !== undefined) {
    updateData.lastUsedDate = new Date();
  }
  
  return await db.externalKeys.update({
    where: { id },
    data: updateData
  });
}

export async function setKeyCooldown(id: string, cooldownUntil: Date) {
  return await db.externalKeys.update({
    where: { id },
    data: { cooldownUntil }
  });
}

export async function getExternalKeyByKey(key: string) {
  return await db.externalKeys.findFirst({
    where: { key }
  });
}

export async function createExternalKey(data: {
  name: string;
  key: string;
  type: ExternalKeysTypeType;
  status?: ExternalKeysStatusType;
  cooldownUntil?: Date;
  monthlyUsageCount?: number;
  monthlyUsage?: number;
  lastResetDate?: Date;
}) {
  // Check if a key with the same value already exists
  const existingKey = await getExternalKeyByKey(data.key);
  if (existingKey) {
    throw new Error(`External key with value '${data.key}' already exists`);
  }

  return await db.externalKeys.create({
    data: {
      ...data,
      status: data.status || ExternalKeysStatusSchema.Values.ACTIVE,
      errors: [],
      usage: 0,
      usageCount: 0,
      currency: "USD",
      monthlyUsageCount: data.monthlyUsageCount || 0,
      monthlyUsage: data.monthlyUsage || 0,
      lastResetDate: data.lastResetDate || new Date(),
      cooldownUntil: data.cooldownUntil || null
    }
  });
}

export async function updateExternalKey(id: string, data: {
  name?: string;
  key?: string;
  status?: ExternalKeysStatusType;
}) {
  // If updating the key, check if the new key value already exists
  if (data.key) {
    const existingKey = await getExternalKeyByKey(data.key);
    if (existingKey && existingKey.id !== id) {
      throw new Error(`External key with value '${data.key}' already exists`);
    }
  }

  return await db.externalKeys.update({
    where: { id },
    data
  });
}

export async function deleteExternalKey(id: string) {
  return await db.externalKeys.delete({
    where: { id }
  });
}

export async function updateExternalKeyErrors(id: string, errors: string[]) {
  return await db.externalKeys.update({
    where: { id },
    data: { errors }
  });
}

export async function getExternalKeysByType(type: ExternalKeysTypeType, status?: ExternalKeysStatusType) {
  return await db.externalKeys.findMany({
    where: {
      type,
      ...(status && { status })
    },
    orderBy: [
      { createdAt: 'desc' },
      { name: 'asc' }
    ]
  });
}
